import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import {
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { type JSX, useCallback, useMemo, useState } from 'react'
import { AppLoader } from '~/components/loader'
import { UsersCommandList } from '~/features/command-lists'
import { useDatastore } from '~/features/datastore'
import { Table } from '~/features/table/ui/table'
import { GroupMutators } from '~/mutators/groups'
import { useTRPC } from '~/services/trpc'
import { SettingsContent, type TitleSegment, useSettingTitle } from '~/templates/settings'

import { FilterCollection } from '@hap/analytics'
import {
  Alert,
  AlertTitle,
  Button,
  Heading2,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@hap/components'

import { DangerZone } from './-components/danger-zone'
import { GroupInfoForm } from './-components/group-info-form'
import { MEMBER_FALLBACK_DATA, createColumns } from './-components/table-config'

export const Route = createFileRoute('/_authenticated/$alias/settings/groups/$groupId')({
  component: RouteComponent,
})

function RouteComponent(): JSX.Element {
  const { groupId } = Route.useParams()
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const trpc = useTRPC()

  const { data: group, isLoading } = useQuery(
    trpc.userGroups.getById.queryOptions({ userGroupId: groupId }),
  )

  const { data: users } = useDatastore(FilterCollection.Users)

  const { addMember } = GroupMutators.useAddMember()

  const navigateToGroups = useCallback(() => {
    navigate({ to: '/$alias/settings/groups', search: location.search })
  }, [location.search, navigate])

  const titleSegments: TitleSegment[] = useMemo(
    () =>
      [{ label: 'Groups', onClick: navigateToGroups }, group?.name].filter(
        Boolean,
      ) as TitleSegment[],
    [navigateToGroups, group?.name],
  )

  useSettingTitle(titleSegments)

  const columns = useMemo(
    () => createColumns(groupId, group?.name || ''),
    [groupId, group?.name],
  )

  const [sorting, setSorting] = useState<SortingState>([{ id: 'user', desc: false }])

  const table = useReactTable({
    columns,
    data: group?.members ?? MEMBER_FALLBACK_DATA,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.userId,
    enableSortingRemoval: false,
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  const handleAddMember = useCallback(
    async (userId: string) => {
      const user = users?.find((u) => u.id === userId)
      await addMember(groupId, {
        id: userId,
        firstName: user?.meta?.firstName,
        lastName: user?.meta?.lastName,
        avatarFileId: user?.meta?.avatarFileId,
      })
    },
    [groupId, addMember, users],
  )

  if (isLoading || !group) return <AppLoader />

  const isArchived = !!group.archivedAt

  return (
    <SettingsContent className="w-full overflow-y-auto">
      {isArchived && (
        <Alert variant="warn">
          <AlertTitle>This group is archived. Reactivate it to make changes.</AlertTitle>
        </Alert>
      )}

      <header className="flex items-start">
        <hgroup className="shrink grow">
          {isArchived ? (
            <div className="space-y-2">
              <h1 className="text-3xl font-bold">{group.name}</h1>
              {group.description && (
                <p className="text-muted-foreground">{group.description}</p>
              )}
            </div>
          ) : (
            <GroupInfoForm
              values={{ name: group.name, description: group.description ?? null }}
              groupId={group.id}
            />
          )}
        </hgroup>
      </header>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <hgroup>
            <Heading2>Members</Heading2>
            <p className="text-accent-foreground">Manage who is a member of the group.</p>
          </hgroup>
          {!isArchived && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="secondary" icon="plus">
                  Add Member
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="end">
                <UsersCommandList
                  excludeIds={group.members?.map((m) => m.userId)}
                  onSelect={(userIds) => {
                    const userId = userIds[0]
                    if (userId) handleAddMember(userId)
                  }}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>

        <Table table={table} />
      </section>

      <DangerZone
        groupId={groupId}
        groupName={group.name}
        isArchived={isArchived}
        onNavigateBack={navigateToGroups}
      />
    </SettingsContent>
  )
}
